import React, { useRef, useEffect, useState } from 'react';

import { Container } from '@soluto-design/react';
import { ButtonProps } from '@soluto-design/react';
import Media, { MediaProps } from '../../molecules/Media';

import BlockTitleLarge, {
    BlockTitleLargeProps,
} from '../../molecules/BlockTitleLarge';

import BlockListSmall, { BlockListSmallProps } from '../BlockListSmall';

import cls from '@soluto-design/styles/cls';

export interface HeroComboSectionProps
    extends Omit<BlockTitleLargeProps, 'rating'> {
    rating?: number;
    ratingLink?: ButtonProps;
    image?: MediaProps['media'];
    links?: BlockListSmallProps;
}

export default function HeroComboSection({
    image,
    rating,
    ratingLink,
    links,
    ...props
}: HeroComboSectionProps) {
    const heroTopRef = useRef<HTMLDivElement>(null);
    const [heroTopHeight, setHeroTopHeight] = useState(0);

    const calculateHeight = () => {
        if (heroTopRef.current) {
            const height = heroTopRef.current.clientHeight;
            setHeroTopHeight(height);
        }
    };

    useEffect(() => {
        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return () => {
            window.removeEventListener('resize', calculateHeight);
        };
    }, []);

    return (
        <section
            className={cls(
                'pt-120 pb-120 relative z-0 bg-background-brand-primary overflow-hidden hero-background',
                heroTopHeight > 600 &&
                    'after:-translate-y-[45%] md:after:-translate-y-1/2',
                heroTopHeight > 1000 &&
                    'after:-translate-y-[40%] md:after:-translate-y-1/2 ',
            )}>
            <Container>
                <div className="relative z-20 flex flex-col gap-80 lg:flex-row lg:gap-48 hd:gap-96">
                    <div
                        ref={heroTopRef}
                        className="py-sm lg:basis-1/2 flex flex-col items-start justify-center">
                        <BlockTitleLarge
                            heroProportions
                            titleTag="h1"
                            preTitleTag="p"
                            {...(!!rating && {
                                rating: { value: rating, link: ratingLink },
                            })}
                            {...props}
                        />
                    </div>
                    <div className="lg:basis-1/2 flex items-center lg:justify-center lg:items-center relative">
                        {!!links?.items?.length && (
                            <div className="w-full lg:pr-margin">
                                <BlockListSmall {...links} />
                            </div>
                        )}
                        {image && (
                            <div className="overflow-hidden relative w-full lg:-right-xl hd:-right-huge">
                                <Media
                                    media={image}
                                    {...(!image?.isImage && {
                                        autoPlay: true,
                                    })}
                                    className="lg:!h-full lg:!w-auto lg:ml-auto"
                                    layout="responsive"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Container>
        </section>
    );
}
